import React, { useState } from "react";
import { Sparkles, Send, ScrollText, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import SovereignAnchor from "./SovereignAnchor";

const cormorant = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const SAMPLES = [
    "Help me migrate 25 workloads off AWS without downtime",
    "We need a SOC 2 readiness report before our Series B",
    "Build me an e-commerce site with subscription mechanics",
    "Pen-test our customer portal — we're going enterprise next quarter",
];

const fmt$ = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

/**
 * The Director's Scout — homepage "I Want Engine".
 * Free-text intake → Claude-powered match → Don Kir-signed Sovereign Proposal.
 */
export default function DirectorScout() {
    const [query, setQuery] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const submit = async (e) => {
        if (e) e.preventDefault();
        const q = query.trim();
        if (q.length < 4) {
            toast.error("Tell the Scout a little more about what you need.");
            return;
        }
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post("/scout/i-want", { query: q });
            setResult(data);
            toast.success("Sovereign Proposal drafted.");
            setTimeout(() => {
                document
                    .querySelector('[data-testid="scout-proposal"]')
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Scout signal lost. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setResult(null);
        setQuery("");
    };

    return (
        <section
            data-testid="director-scout"
            className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-20"
        >
            {/* Aurora wash */}
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[10%] left-[5%] w-[40%] h-[60%] rounded-full blur-[140px] opacity-30 bg-[hsl(40_60%_45%/0.20)]" />
                <div className="absolute bottom-[10%] right-[5%] w-[40%] h-[60%] rounded-full blur-[140px] opacity-30 bg-[hsl(153_50%_25%/0.30)]" />
            </div>

            {/* Header */}
            <div className="relative z-10 text-center mb-10 max-w-2xl mx-auto">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-5 text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] font-mono border border-[hsl(40_50%_35%/0.4)] bg-[hsl(151_55%_7%/0.6)]">
                    <Sparkles size={11} /> The Director's Scout · I-Want Engine
                </div>
                <h2
                    className="font-heading tracking-tighter leading-[0.95] text-foreground"
                    style={{ fontSize: "clamp(2.5rem, 5vw, 4.25rem)" }}
                    data-testid="scout-headline"
                >
                    What do you{" "}
                    <span className="gold-gradient-text italic">need?</span>
                </h2>
                <p
                    className="mt-5 text-lg text-foreground/75"
                    style={cormorant}
                >
                    "You tell us the goal; we provide the armor."
                </p>
            </div>

            {/* Input box */}
            {!result && (
                <form
                    onSubmit={submit}
                    className="relative z-10 max-w-3xl mx-auto"
                    data-testid="scout-form"
                >
                    <div className="relative glass rounded-2xl border border-[hsl(40_40%_25%/0.45)] focus-within:border-[hsl(45_85%_68%)] transition-colors overflow-hidden">
                        <textarea
                            data-testid="scout-input"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Describe what your fleet needs in plain language. The Scout will match the right platform, the right covenant, the right yield..."
                            rows={4}
                            disabled={busy}
                            className="w-full px-6 py-5 bg-transparent focus:outline-none text-base resize-none text-foreground placeholder:text-muted-foreground/60"
                        />
                        <div className="flex items-center justify-between px-4 py-3 border-t border-[hsl(40_40%_25%/0.3)] bg-[hsl(151_55%_5%/0.45)]">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70 font-mono">
                                Routed through the Directorate · Don Kir reviews each proposal
                            </div>
                            <button
                                type="submit"
                                disabled={busy || query.trim().length < 4}
                                data-testid="scout-submit"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-40"
                            >
                                {busy ? (
                                    <>
                                        <Loader2 size={15} className="animate-spin" /> Scout reading...
                                    </>
                                ) : (
                                    <>
                                        <Send size={14} /> Dispatch Scout
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Sample prompts */}
                    <div className="mt-5 flex flex-wrap items-center gap-2 justify-center" data-testid="scout-samples">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70 font-mono mr-1">
                            Try:
                        </span>
                        {SAMPLES.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setQuery(s)}
                                disabled={busy}
                                className="text-xs px-3 py-1.5 rounded-full border border-[hsl(40_40%_25%/0.4)] text-muted-foreground hover:text-[hsl(45_85%_75%)] hover:border-[hsl(45_85%_68%/0.6)] transition-colors disabled:opacity-50"
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </form>
            )}

            {/* Sovereign Proposal */}
            {result && <ScoutProposal result={result} onReset={reset} />}
        </section>
    );
}

function ScoutProposal({ result, onReset }) {
    const { proposal, matched_services, signature } = result;
    return (
        <div
            data-testid="scout-proposal"
            className="relative z-10 max-w-4xl mx-auto"
        >
            <div
                className="relative overflow-hidden rounded-3xl border border-[hsl(40_50%_40%/0.5)]"
                style={{
                    background:
                        "linear-gradient(180deg, hsl(151 60% 6%) 0%, hsl(40 35% 8%) 100%)",
                    boxShadow: "0 30px 80px -20px hsl(40 60% 45% / 0.30)",
                }}
            >
                {/* Anchor watermark */}
                <div
                    aria-hidden="true"
                    className="absolute -bottom-8 -right-8 pointer-events-none select-none text-[hsl(45_85%_68%)]"
                    style={{ opacity: 0.07, transform: "rotate(-8deg)" }}
                >
                    <SovereignAnchor size={320} />
                </div>

                {/* Document header */}
                <div className="relative p-8 sm:p-10 border-b border-[hsl(40_40%_25%/0.4)] flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <ScrollText size={16} className="text-[hsl(45_85%_68%)]" />
                        <div className="text-[10px] uppercase tracking-[0.45em] text-[hsl(45_85%_68%)] font-mono">
                            Sovereign Proposal · Drafted by the Directorate
                        </div>
                    </div>
                    <button
                        onClick={onReset}
                        data-testid="scout-reset"
                        className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors flex items-center gap-1.5"
                    >
                        <RefreshCw size={11} /> Brief the Scout again
                    </button>
                </div>

                {/* Title + summary */}
                <div className="relative p-8 sm:p-10">
                    <h3
                        className="font-heading text-3xl sm:text-5xl tracking-tighter leading-[1.05]"
                        data-testid="scout-proposal-title"
                    >
                        {proposal.proposal_title}
                    </h3>
                    {proposal.summary && (
                        <p
                            className="mt-3 text-base text-foreground/70 italic max-w-2xl"
                            style={cormorant}
                        >
                            {proposal.summary}
                        </p>
                    )}

                    {/* Body */}
                    <div className="mt-6 pt-6 border-t border-[hsl(40_40%_25%/0.3)]">
                        <p
                            className="text-base sm:text-lg leading-relaxed text-foreground/85 whitespace-pre-line"
                            data-testid="scout-proposal-body"
                        >
                            {proposal.proposal_body}
                        </p>
                    </div>

                    {/* Matched services */}
                    {matched_services && matched_services.length > 0 && (
                        <div className="mt-8" data-testid="scout-matched">
                            <div className="text-[10px] uppercase tracking-[0.4em] text-[hsl(45_85%_68%)] font-mono mb-3">
                                Matched Platforms
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {matched_services.map((s) => (
                                    <div
                                        key={s.code}
                                        className="rounded-xl border border-[hsl(40_40%_25%/0.45)] p-4 bg-[hsl(151_55%_5%/0.5)]"
                                    >
                                        <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-mono">
                                            <span>{s.category}</span>
                                            <span className="text-[hsl(45_85%_68%)]">{s.code}</span>
                                        </div>
                                        <div className="mt-2 font-heading text-lg leading-tight">
                                            {s.name}
                                        </div>
                                        <div className="mt-3 flex items-center justify-between text-sm">
                                            <span className="text-foreground/70">{s.duration}</span>
                                            <span className="font-mono gold-gradient-text">
                                                {fmt$(s.price)}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Estimate strip */}
                    {(proposal.estimated_range || proposal.estimated_timeline) && (
                        <div
                            className="mt-7 grid sm:grid-cols-2 gap-3"
                            data-testid="scout-estimate"
                        >
                            {proposal.estimated_range && (
                                <div className="rounded-xl border border-[hsl(40_40%_25%/0.4)] p-4">
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                                        Investment range
                                    </div>
                                    <div className="mt-1 font-heading text-2xl gold-gradient-text">
                                        {proposal.estimated_range}
                                    </div>
                                </div>
                            )}
                            {proposal.estimated_timeline && (
                                <div className="rounded-xl border border-[hsl(40_40%_25%/0.4)] p-4">
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                                        Timeline
                                    </div>
                                    <div className="mt-1 font-heading text-2xl">
                                        {proposal.estimated_timeline}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* CTA */}
                    <div className="mt-9 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <Link
                            to="/foundry/control"
                            data-testid="scout-cta-charter"
                            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        >
                            {proposal.recommended_action || "File a charter brief"} <ArrowRight size={16} />
                        </Link>
                        <a
                            href={`mailto:${signature.alias_email}?subject=Sovereign%20Proposal%20Inquiry%20·%20${encodeURIComponent(proposal.proposal_title || "")}`}
                            data-testid="scout-cta-email"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[hsl(40_40%_25%/0.6)] text-foreground/85 hover:border-[hsl(45_85%_68%)] hover:text-[hsl(45_85%_75%)] transition-colors"
                        >
                            Reply to {signature.name} directly
                        </a>
                    </div>

                    {/* Signature block */}
                    <div
                        className="mt-10 pt-6 border-t border-[hsl(40_40%_25%/0.3)]"
                        data-testid="scout-signature"
                    >
                        <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)] font-mono">
                            Signed
                        </div>
                        <div
                            className="mt-1 font-heading text-3xl gold-gradient-text leading-none"
                            style={{ letterSpacing: "-0.01em" }}
                        >
                            {signature.name}
                        </div>
                        <div className="mt-1 text-xs uppercase tracking-[0.3em] text-muted-foreground">
                            {signature.title}
                        </div>
                        <p
                            className="mt-3 text-sm italic text-foreground/65"
                            style={cormorant}
                        >
                            "{signature.mission_line}"
                        </p>
                        <div className="mt-3 text-[10px] text-muted-foreground/70 font-mono">
                            {signature.alias_email} · {signature.coordinate}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
